import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Container,
  AppBar,
  Toolbar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
  RadialLinearScale,
} from "chart.js";
import { Line, Bar, Doughnut, Scatter } from "react-chartjs-2";
import {
  TrendingUp,
  TrendingDown,
  AttachMoney as DollarSign,
  ShowChart as Activity,
  CallMade as Inflow,
  CallReceived as Outflow,
  SwapHoriz as NetFlow,
 
  Assessment,
} from "@mui/icons-material";
import Layout from "../../components/layout/ChartsLayout";
import config from "../../config";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
  RadialLinearScale
);

const theme = createTheme({
  palette: {
    primary: {
      main: "#EB5A3C",
      light: "#FF7A5C",
      dark: "#C54428",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#F8EEDB",
      light: "#FFFAF0",
      dark: "#E6D4B7",
      contrastText: "#333333",
    },
    background: {
      default: "#F8EEDB",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#333333",
      secondary: "#666666",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: "0 4px 20px rgba(235, 90, 60, 0.1)",
          transition: "all 0.3s ease-in-out",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 8px 30px rgba(235, 90, 60, 0.15)",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
  },
});

const CashFlowDashboard = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { API_BASE_URL } = config;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${API_BASE_URL}/api/v1/getFinancialReports`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          setData(result);
          setError(null);
        } else {
          throw new Error(result.message || "Failed to fetch data");
        }
      } catch (err) {
        console.error("Error fetching financial data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [API_BASE_URL]);

  // const excelDateToJS = (serial) => {
  //   if (!serial) return "N/A";
  //   const excelStartDate = new Date(1900, 0, 1);
  //   const jsDate = new Date(
  //     excelStartDate.getTime() + (serial - 2) * 24 * 60 * 60 * 1000
  //   );
  //   return jsDate.toLocaleDateString();
  // };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "$0";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // const formatPercent = (value) => {
  //   if (value === null || value === undefined) return "0%";
  //   return `${(value * 100).toFixed(1)}%`;
  // };

  if (loading) {
    return (
      <Layout>
        <ThemeProvider theme={theme}>
          <Box
            sx={{
              minHeight: "100vh",
              background: "linear-gradient(135deg, #F8EEDB 0%, #FFFAF0 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Paper
                elevation={3}
                sx={{
                  p: 4,
                  textAlign: "center",
                  borderRadius: 4,
                }}
              >
                <CircularProgress size={60} sx={{ color: "#EB5A3C", mb: 2 }} />
                <Typography variant="h6" color="text.primary">
                  Loading Cash Flow Data...
                </Typography>
              </Paper>
            </motion.div>
          </Box>
        </ThemeProvider>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <ThemeProvider theme={theme}>
          <Box
            sx={{
              minHeight: "100vh",
              background: "linear-gradient(135deg, #F8EEDB 0%, #FFFAF0 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 2,
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Alert
                severity="error"
                sx={{
                  maxWidth: 600,
                  borderRadius: 2,
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Error loading cash flow data
                </Typography>
                <Typography variant="body2">{error}</Typography>
              </Alert>
            </motion.div>
          </Box>
        </ThemeProvider>
      </Layout>
    );
  }

  if (!data?.success || !data?.data?.reports?.CashFlow) {
    return (
      <ThemeProvider theme={theme}>
        <Box
          sx={{
            minHeight: "100vh",
            background: "linear-gradient(135deg, #F8EEDB 0%, #FFFAF0 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Alert severity="warning">No cash flow data available</Alert>
        </Box>
      </ThemeProvider>
    );
  }

  const cashFlowData = data.data.reports.CashFlow?.data?.Sheet1 || [];

  // Calculate key metrics
  const totalOperatingCashFlow = cashFlowData.reduce(
    (sum, item) => sum + (item["Operating Cash Flow"] || 0),
    0
  );
  const totalInvestingCashFlow = cashFlowData.reduce(
    (sum, item) => sum + (item["Investing Cash Flow"] || 0),
    0
  );
  const totalFinancingCashFlow = cashFlowData.reduce(
    (sum, item) => sum + (item["Financing Cash Flow"] || 0),
    0
  );
  const totalNetCashFlow = cashFlowData.reduce(
    (sum, item) => sum + (item["Net Cash Flow"] || 0),
    0
  );
  const currentCashBalance =
    cashFlowData.length > 0
      ? cashFlowData[cashFlowData.length - 1]["Cash Balance"] || 0
      : 0;

  // Calculate trends
  const recentPeriods = cashFlowData.slice(-3);
  const previousPeriods = cashFlowData.slice(-6, -3);
  const avgRecentCashFlow =
    recentPeriods.reduce((sum, item) => sum + (item["Net Cash Flow"] || 0), 0) /
    recentPeriods.length;
  const avgPreviousCashFlow =
    previousPeriods.reduce(
      (sum, item) => sum + (item["Net Cash Flow"] || 0),
      0
    ) / previousPeriods.length;
  const cashFlowTrend =
    previousPeriods.length > 0
      ? ((avgRecentCashFlow - avgPreviousCashFlow) /
          Math.abs(avgPreviousCashFlow)) *
        100
      : 0;

  // Prepare chart data
  const labels = cashFlowData.map((_, index) => `Period ${index + 1}`);

  // Cash Flow Trend Chart
  const cashFlowTrendData = {
    labels: labels,
    datasets: [
      {
        label: "Operating Cash Flow",
        data: cashFlowData.map((item) => item["Operating Cash Flow"] || 0),
        borderColor: "#EB5A3C",
        backgroundColor: "rgba(235, 90, 60, 0.1)",
        fill: true,
        tension: 0.4,
      },
      {
        label: "Investing Cash Flow",
        data: cashFlowData.map((item) => item["Investing Cash Flow"] || 0),
        borderColor: "#FF7A5C",
        backgroundColor: "rgba(255, 122, 92, 0.1)",
        fill: true,
        tension: 0.4,
      },
      {
        label: "Financing Cash Flow",
        data: cashFlowData.map((item) => item["Financing Cash Flow"] || 0),
        borderColor: "#C54428",
        backgroundColor: "rgba(197, 68, 40, 0.1)",
        fill: true,
        tension: 0.4,
      },
      {
        label: "Net Cash Flow",
        data: cashFlowData.map((item) => item["Net Cash Flow"] || 0),
        borderColor: "#B8361F",
        backgroundColor: "rgba(184, 54, 31, 0.2)",
        fill: false,
        tension: 0.4,
        borderWidth: 3,
      },
    ],
  };

  // Cash Balance Growth Chart
  const cashBalanceData = {
    labels: labels,
    datasets: [
      {
        label: "Cash Balance",
        data: cashFlowData.map((item) => item["Cash Balance"] || 0),
        borderColor: "#EB5A3C",
        backgroundColor: "rgba(235, 90, 60, 0.1)",
        fill: true,
        tension: 0.4,
        pointBackgroundColor: "#EB5A3C",
        pointBorderColor: "#FFFFFF",
        pointBorderWidth: 2,
        pointRadius: 6,
      },
    ],
  };

  // Cash Flow Components Bar Chart
  const cashFlowComponentsData = {
    labels: labels.slice(-12),
    datasets: [
      {
        label: "Operating",
        data: cashFlowData
          .slice(-12)
          .map((item) => item["Operating Cash Flow"] || 0),
        backgroundColor: "#EB5A3C",
        borderRadius: 8,
      },
      {
        label: "Investing",
        data: cashFlowData
          .slice(-12)
          .map((item) => item["Investing Cash Flow"] || 0),
        backgroundColor: "#FF7A5C",
        borderRadius: 8,
      },
      {
        label: "Financing",
        data: cashFlowData
          .slice(-12)
          .map((item) => item["Financing Cash Flow"] || 0),
        backgroundColor: "#C54428",
        borderRadius: 8,
      },
    ],
  };

  // Cash Flow Distribution Pie Chart
  const cashFlowDistributionData = {
    labels: [
      "Operating Cash Flow",
      "Investing Cash Flow",
      "Financing Cash Flow",
    ],
    datasets: [
      {
        data: [
          Math.abs(totalOperatingCashFlow),
          Math.abs(totalInvestingCashFlow),
          Math.abs(totalFinancingCashFlow),
        ],
        backgroundColor: ["#EB5A3C", "#FF7A5C", "#C54428"],
        borderWidth: 0,
      },
    ],
  };

  // Scatter Plot for Cash Flow vs Balance
  const scatterData = {
    datasets: [
      {
        label: "Cash Flow vs Balance",
        data: cashFlowData.map((item) => ({
          x: item["Net Cash Flow"] || 0,
          y: item["Cash Balance"] || 0,
        })),
        backgroundColor: "rgba(235, 90, 60, 0.6)",
        borderColor: "#EB5A3C",
        borderWidth: 2,
        pointRadius: 8,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            return "$" + (value / 1000).toFixed(0) + "K";
          },
        },
      },
    },
  };

  const StatCard = ({ title, value, change, icon: Icon, color, subtitle }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      whileHover={{ scale: 1.02 }}
    >
      <Card>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography color="text.secondary" gutterBottom variant="body2">
                {title}
              </Typography>
              <Typography
                variant="h5"
                component="div"
                sx={{ fontWeight: "bold", mb: 1 }}
              >
                {value}
              </Typography>
              {subtitle && (
                <Typography variant="body2" color="text.secondary">
                  {subtitle}
                </Typography>
              )}
              {change !== undefined && (
                <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                  {change >= 0 ? (
                    <TrendingUp
                      size={16}
                      style={{ color: "#4CAF50", marginRight: 4 }}
                    />
                  ) : (
                    <TrendingDown
                      size={16}
                      style={{ color: "#F44336", marginRight: 4 }}
                    />
                  )}
                  <Typography
                    variant="body2"
                    sx={{
                      color: change >= 0 ? "#4CAF50" : "#F44336",
                      fontWeight: "medium",
                    }}
                  >
                    {Math.abs(change).toFixed(1)}%
                  </Typography>
                </Box>
              )}
            </Box>
            <Avatar
              sx={{
                backgroundColor: color,
                width: 56,
                height: 56,
              }}
            >
              <Icon size={24} color="white" />
            </Avatar>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );

  const TabPanel = ({ children, value, index }) => (
    <AnimatePresence mode="wait">
      {value === index && (
        <motion.div
          key={index}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
        >
          <Box sx={{ py: 3 }}>{children}</Box>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <Layout>
      <ThemeProvider theme={theme}>
        <Box
          sx={{
            minHeight: "100vh",
            background: "linear-gradient(135deg, #F8EEDB 0%, #FFFAF0 100%)",
          }}
        >
          {/* Header */}
          <AppBar
            position="sticky"
            elevation={0}
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
            }}
          >
            <Toolbar>
              <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
                <Avatar sx={{ backgroundColor: "#EB5A3C", mr: 2 }}>
                  <Activity />
                </Avatar>
                <Box>
                  <Typography
                    variant="h5"
                    sx={{ color: "#333", fontWeight: "bold" }}
                  >
                    Cash Flow Analytics
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#666" }}>
                    Comprehensive Cash Flow Monitoring & Analysis
                  </Typography>
                </Box>
              </Box>
            </Toolbar>
          </AppBar>

          <Container maxWidth="xl" sx={{ py: 4 }}>
            {/* Key Metrics */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Current Cash Balance"
                  value={formatCurrency(currentCashBalance)}
                  change={cashFlowTrend}
                  icon={DollarSign}
                  color="#EB5A3C"
                  subtitle="Latest Period"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Total Net Cash Flow"
                  value={formatCurrency(totalNetCashFlow)}
                  icon={NetFlow}
                  color="#FF7A5C"
                  subtitle="All Periods"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Operating Cash Flow"
                  value={formatCurrency(totalOperatingCashFlow)}
                  icon={Inflow}
                  color="#C54428"
                  subtitle="Total Generated"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Investment Outflows"
                  value={formatCurrency(Math.abs(totalInvestingCashFlow))}
                  icon={Outflow}
                  color="#B8361F"
                  subtitle="Total Invested"
                />
              </Grid>
            </Grid>

            {/* Navigation Tabs */}
            <Paper sx={{ mb: 3 }}>
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                variant="fullWidth"
                sx={{
                  "& .MuiTab-root": {
                    fontWeight: 600,
                    textTransform: "none",
                  },
                  "& .Mui-selected": {
                    color: "#EB5A3C !important",
                  },
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#EB5A3C",
                  },
                }}
              >
                <Tab label="Cash Flow Trends" />
                <Tab label="Cash Balance Growth" />
                <Tab label="Component Analysis" />
                <Tab label="Distribution & Insights" />
                <Tab label="Detailed Records" />
              </Tabs>
            </Paper>

            {/* Tab Content */}
            <TabPanel value={activeTab} index={0}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                  >
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Cash Flow Components Over Time
                        </Typography>
                        <Box sx={{ height: 500 }}>
                          <Line
                            data={cashFlowTrendData}
                            options={chartOptions}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
              <Grid container spacing={3}>
                <Grid item xs={12} lg={8}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                  >
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Cash Balance Evolution
                        </Typography>
                        <Box sx={{ height: 400 }}>
                          <Line data={cashBalanceData} options={chartOptions} />
                        </Box>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
                <Grid item xs={12} lg={4}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                  >
                    <Card sx={{ height: "100%" }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Cash Balance Highlights
                        </Typography>
                        <List>
                          <ListItem>
                            <ListItemAvatar>
                              <Avatar sx={{ backgroundColor: "#4CAF50" }}>
                                <TrendingUp />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary="Highest Balance"
                              secondary={formatCurrency(
                                Math.max(
                                  ...cashFlowData.map(
                                    (item) => item["Cash Balance"] || 0
                                  )
                                )
                              )}
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemAvatar>
                              <Avatar sx={{ backgroundColor: "#F44336" }}>
                                <TrendingDown />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary="Lowest Balance"
                              secondary={formatCurrency(
                                Math.min(
                                  ...cashFlowData.map(
                                    (item) => item["Cash Balance"] || 0
                                  )
                                )
                              )}
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemAvatar>
                              <Avatar sx={{ backgroundColor: "#EB5A3C" }}>
                                <Assessment />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary="Average Balance"
                              secondary={formatCurrency(
                                cashFlowData.reduce(
                                  (sum, item) =>
                                    sum + (item["Cash Balance"] || 0),
                                  0
                                ) / cashFlowData.length
                              )}
                            />
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel value={activeTab} index={2}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                  >
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Cash Flow Components Breakdown (Last 12 Periods)
                        </Typography>
                        <Box sx={{ height: 400 }}>
                          <Bar
                            data={cashFlowComponentsData}
                            options={chartOptions}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel value={activeTab} index={3}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                  >
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Cash Flow Distribution
                        </Typography>
                        <Box sx={{ height: 400 }}>
                          <Doughnut
                            data={cashFlowDistributionData}
                            options={{
                              responsive: true,
                              maintainAspectRatio: false,
                            }}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
                <Grid item xs={12} md={6}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                  >
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Cash Flow vs Balance Correlation
                        </Typography>
                        <Box sx={{ height: 400 }}>
                          <Scatter
                            data={scatterData}
                            options={{
                              responsive: true,
                              maintainAspectRatio: false,
                              plugins: {
                                legend: {
                                  position: "top",
                                },
                              },
                              scales: {
                                x: {
                                  title: {
                                    display: true,
                                    text: "Net Cash Flow",
                                  },
                                  ticks: {
                                    callback: function (value) {
                                      return (
                                        "$" + (value / 1000).toFixed(0) + "K"
                                      );
                                    },
                                  },
                                },
                                y: {
                                  title: {
                                    display: true,
                                    text: "Cash Balance",
                                  },
                                  beginAtZero: true,
                                  ticks: {
                                    callback: function (value) {
                                      return (
                                        "$" + (value / 1000).toFixed(0) + "K"
                                      );
                                    },
                                  },
                                },
                              },
                            }}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel value={activeTab} index={4}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Detailed Cash Flow Records
                    </Typography>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Period</TableCell>
                            <TableCell align="right">Operating</TableCell>
                            <TableCell align="right">Investing</TableCell>
                            <TableCell align="right">Financing</TableCell>
                            <TableCell align="right">Net Flow</TableCell>
                            <TableCell align="right">Cash Balance</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {cashFlowData.map((item, index) => (
                            <TableRow key={index}>
                              <TableCell>Period {index + 1}</TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={formatCurrency(
                                    item["Operating Cash Flow"] || 0
                                  )}
                                  color={
                                    item["Operating Cash Flow"] >= 0
                                      ? "success"
                                      : "error"
                                  }
                                  size="small"
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={formatCurrency(
                                    item["Investing Cash Flow"] || 0
                                  )}
                                  color={
                                    item["Investing Cash Flow"] >= 0
                                      ? "success"
                                      : "error"
                                  }
                                  size="small"
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={formatCurrency(
                                    item["Financing Cash Flow"] || 0
                                  )}
                                  color={
                                    item["Financing Cash Flow"] >= 0
                                      ? "success"
                                      : "error"
                                  }
                                  size="small"
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: "bold",
                                    color:
                                      (item["Net Cash Flow"] || 0) >= 0
                                        ? "#4CAF50"
                                        : "#F44336",
                                  }}
                                >
                                  {formatCurrency(item["Net Cash Flow"] || 0)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: "medium" }}
                                >
                                  {formatCurrency(item["Cash Balance"] || 0)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </motion.div>
            </TabPanel>
          </Container>
        </Box>
      </ThemeProvider>
    </Layout>
  );
};

export default CashFlowDashboard;
